import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, HostListener } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { filter, map, mergeMap } from 'rxjs';
import { SEOService } from './core/shared-services/SEO.service';
import { GtmService } from './core/shared-services/gtm.service';
import { MatMenuModule } from '@angular/material/menu';
import { AuthService } from './core/auth/auth.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { DataPreloaderService } from './core/shared-services/data-preloader.service';
import { WhatsappButtonComponent } from './shared/components/whatsapp-button/whatsapp-button.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    animations: fuseAnimations,
    imports: [RouterOutlet, MatButtonModule, MatIconModule, MatMenuModule, CommonModule, WhatsappButtonComponent],
})
export class AppComponent {
    /**
     * Constructor
     */
    activePage: string = 'dashboard';
    token: boolean;
    sidebarOpen = true;
    showLabels = true;
    memberUrl: boolean;
    isMobile = window.innerWidth < 1024; // Use lg breakpoint for consistency

    sidebarItems = [
        {
            label: 'My Profile',
            icon: 'account_circle',
            route: '/dashboard/profile',
            page: 'profile'
        },
        {
            label: 'Dashboard',
            icon: 'dashboard',
            route: '/dashboard',
            page: 'Dashboard'
        },
        {
            label: 'Active Services',
            icon: 'wifi',
            route: '/dashboard/services',
            page: 'services'
        },
        {
            label: 'Billing & Statements',
            icon: 'credit_card',
            route: '/dashboard/invoices',
            page: 'invoices'
        },
        {
            label: 'Support Requests',
            icon: 'contact_support',
            route: '/dashboard/requests',
            page: 'requests'
        },
        {
            label: 'Pay Now',
            icon: 'payments',
            route: '/dashboard/balances',
            page: 'balances'
        }
    ];


    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private _seoService: SEOService,
        private activatedRoute: ActivatedRoute,
        public router: Router,
        private gtmService: GtmService,
        public authService: AuthService,
        private dataPreloaderService: DataPreloaderService

    ) {

    }

    ngOnInit() {
        
        this.token = !!this.authService.accessToken;
        // Set initial sidebar state based on device type
        this.isMobile = window.innerWidth < 1024;
        if(this.isMobile){
            this.sidebarOpen = false; // Start closed on mobile
            this.showLabels = true; // Always show labels on mobile when open
        }
  

        // Preload all Strapi data for main pages at startup
        if (isPlatformBrowser(this.platformId)) {
            this.dataPreloaderService.preloadAllData().subscribe({
                next: () => {
                    console.log('All page data preloaded successfully');
                },
                error: (error) => {
                    console.error('Failed to preload page data:', error);
                }
            });
        }

        // Initialize active state from current URL
        const currentUrl = this.router.url;
        this.memberUrl = currentUrl.includes('/dashboard');
        this.updateActiveFromUrl(currentUrl);

        // Update active state on navigation changes
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                this.memberUrl = url.includes('/dashboard');
                this.updateActiveFromUrl(url);
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Whatsapp redirection
     */
    public redirectToWhatsApp() {
        if (isPlatformBrowser(this.platformId)) {
            window.open('https://api.whatsapp.com/send/?phone=+27824805401&text=Please+can+you+assist+mee', '_blank');
        }
    }


    logout() {
        this.authService.signOut();
        this.router.navigateByUrl("/");
    }

    setActive(page: string): void {
        // Close sidebar on mobile when navigating to a new page
        if(this.isMobile && page !== this.activePage && this.sidebarOpen){
            this.sidebarOpen = false;
        }
        this.activePage = page;
    }


    toggleSidebar() {
        this.sidebarOpen = !this.sidebarOpen;

        // Handle label visibility for desktop only
        if (!this.isMobile) {
            if (!this.sidebarOpen) {
                // Hide labels immediately when collapsing on desktop
                this.showLabels = false;
            } else {
                // Wait for the animation to finish before showing labels on desktop
                setTimeout(() => {
                    this.showLabels = true;
                }, 200); // Match your Tailwind `duration-300`
            }
        } else {
            // Always show labels on mobile when sidebar is open
            this.showLabels = true;
        }
    }

    goHome(){
        this.router.navigateByUrl("/");
    }

    /**
     * Update activePage based on current URL by finding the
     * sidebar item whose route is the longest prefix of the URL.
     */
    private updateActiveFromUrl(url: string): void {
        if (!url) {
            return;
        }
        const match = this.sidebarItems
            .filter(item => url.startsWith(item.route))
            .sort((a, b) => b.route.length - a.route.length)[0];

        if (match) {
            this.activePage = match.page;
        }
    }

 
@HostListener('window:resize', ['$event'])
onResize(event: any) {
    const newIsMobile = event.target.innerWidth < 1024;
    
    // Handle device type change
    if (newIsMobile !== this.isMobile) {
        this.isMobile = newIsMobile;
        
        if (this.isMobile) {
            // Switched to mobile: close sidebar and ensure labels are shown when open
            this.sidebarOpen = false;
            this.showLabels = true;
        } else {
            // Switched to desktop: open sidebar and handle labels properly
            this.sidebarOpen = true;
            this.showLabels = true;
        }
    }
}

@HostListener('document:click', ['$event'])
onDocumentClick(event: MouseEvent) {
    if (!this.isMobile || !this.sidebarOpen) {
        return; // Only act on mobile when sidebar is open
    }

    const sidebarEl = document.querySelector('.sidebar-container');
    const hamburgerBtn = document.querySelector('.fixed.top-4.left-4');
    
    // Don't close if clicking on sidebar or hamburger button
    if (sidebarEl && !sidebarEl.contains(event.target as Node) &&
        hamburgerBtn && !hamburgerBtn.contains(event.target as Node)) {
        this.sidebarOpen = false;
    }
}
}
